import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useBranch, withBranch } from "@/lib/branch";
import { AuthImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { fmtDate, FREQ_LABELS, FREQ_UNIT, LOAN_STATUS, loanTenor, rupiah } from "@/lib/format";

const Item = ({ k, v }) => <tr><td className="py-1 pr-4 align-top text-neutral-600 w-48">{k}</td><td className="py-1 font-medium">{v}</td></tr>;

const Letterhead = ({ s, logoSrc, cabang }) => (
  <div className="flex items-center gap-4 border-b-4 border-double border-neutral-800 pb-4" data-testid="contract-letterhead">
    {logoSrc && <img src={logoSrc} alt="Logo" className="h-16 w-16 object-contain" />}
    <div className="flex-1 text-center">
      <p className="font-heading text-xl sm:text-2xl font-bold uppercase tracking-wide" data-testid="contract-app-name">{s.app_name}</p>
      {cabang && <p className="text-sm font-semibold">Kantor Cabang {cabang}</p>}
      {s.receipt_header && <p className="text-xs text-neutral-600 whitespace-pre-line">{s.receipt_header}</p>}
    </div>
  </div>
);

const Terms = ({ l }) => (
  <div className="space-y-6 text-sm leading-relaxed">
    <p>Pada tanggal <b>{fmtDate(l.loan_date || l.created_at)}</b>, anggota yang bertanda tangan di bawah ini mengajukan pinjaman kepada koperasi dengan ketentuan sebagai berikut:</p>
    <table className="w-full"><tbody>
      <Item k="Nama Anggota" v={l.member_name} />
      <Item k="NIK" v={l.nik} />
      <Item k="Resort / Petugas" v={`${l.resort_name || "-"} / ${l.petugas_name || "-"}`} />
      <Item k="Pokok Pinjaman" v={rupiah(l.principal)} />
      <Item k="Margin" v={`${l.margin_rate ? `${l.margin_rate}% flat` : `${l.interest_rate}%/bln`} (${rupiah(l.interest_total)})`} />
      {l.admin_fee !== undefined && <Item k="Biaya Admin" v={rupiah(l.admin_fee)} />}
      {l.savings_cut !== undefined && <Item k="Potongan Simpanan" v={rupiah(l.savings_cut)} />}
      {l.net_disbursed !== undefined && <Item k="Diterima Anggota" v={rupiah(l.net_disbursed)} />}
      <Item k="Total Pengembalian" v={rupiah(l.total_payable)} />
      <Item k="Angsuran" v={`${rupiah(l.installment_amount)} per ${FREQ_UNIT[l.frequency]} \u00b7 ${loanTenor(l)}x ${FREQ_LABELS[l.frequency].toLowerCase()}`} />
      {l.collateral_type && <Item k="Jaminan" v={l.collateral_type} />}
      {l.purpose && <Item k="Keperluan" v={l.purpose} />}
    </tbody></table>
    <p>Anggota menyatakan telah membaca dan menyetujui ketentuan pinjaman di atas, serta bersedia membayar angsuran sesuai jadwal hingga lunas.</p>
  </div>
);

const Signatures = ({ l, cabang }) => (
  <div className="grid grid-cols-2 gap-8 pt-6 text-sm text-center" data-testid="contract-signatures">
    <div>
      <p>Petugas / Koperasi<br />&nbsp;</p>
      {l.petugas_id ? <AuthImage path={`/users/${l.petugas_id}/signature`} alt="Tanda tangan petugas" className="h-24 mx-auto object-contain" testId="contract-petugas-signature" fallback={<div className="h-24" />} /> : <div className="h-24" />}
      <p className="font-semibold border-t border-neutral-500 pt-1 mx-6">{l.petugas_name || l.approved_by_name || l.created_by_name || "-"}</p>
    </div>
    <div>
      <p>{cabang ? `${cabang}, ` : ""}{fmtDate(l.signature_at || l.loan_date || l.created_at)}<br />Anggota</p>
      {l.signature_path
        ? <AuthImage path={`/loans/${l.id}/signature`} alt="Tanda tangan anggota" className="h-24 mx-auto object-contain" testId="contract-signature" />
        : <div className="h-24 grid place-items-center text-xs text-neutral-500" data-testid="contract-no-signature">Belum ditandatangani</div>}
      <p className="font-semibold border-t border-neutral-500 pt-1 mx-6">{l.member_name}</p>
    </div>
  </div>
);

export default function LoanContractPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, logoSrc } = useSettings();
  const s = withBranch(settings, useBranch());
  const [l, setL] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/loans/${id}`).then((r) => setL(r.data)).catch((e) => { setErr(errMsg(e)); toast.error(errMsg(e)); }); }, [id]);
  if (err) return <p className="py-10 text-center text-muted-foreground" data-testid="contract-error">{err}</p>;
  if (!l) return <p className="text-muted-foreground">Memuat...</p>;
  return (
    <div>
      <div className="flex items-center justify-between mb-4 print:hidden">
        <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-muted-foreground" data-testid="contract-back"><ArrowLeft className="h-4 w-4 mr-1" />Kembali</button>
        <Button className="rounded-full h-10 px-5" onClick={() => window.print()} data-testid="contract-print"><Printer className="h-4 w-4 mr-1.5" />Cetak</Button>
      </div>
      <div id="loan-contract" className="mx-auto max-w-3xl bg-white text-neutral-900 rounded-2xl shadow-sm border p-6 sm:p-10 space-y-6" data-testid="loan-contract">
        <Letterhead s={s} logoSrc={logoSrc} cabang={user.cabang_name} />
        <div className="text-center">
          <p className="font-heading text-lg font-bold underline underline-offset-4">SURAT PERJANJIAN PINJAMAN</p>
          <p className="text-sm">No. {l.loan_no || "-"} · Status {LOAN_STATUS[l.status].label}</p>
        </div>
        <Terms l={l} />
        <Signatures l={l} cabang={user.cabang_name} />
      </div>
    </div>
  );
}
